export function playArrivalNotificationTone() {
  try {
    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;

    if (!AudioContextConstructor) {
      return;
    }

    const audioContext = new AudioContextConstructor();
    const startedAt = audioContext.currentTime;
    const gainNode = audioContext.createGain();

    gainNode.connect(audioContext.destination);
    gainNode.gain.setValueAtTime(0.0001, startedAt);
    gainNode.gain.exponentialRampToValueAtTime(0.18, startedAt + 0.03);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, startedAt + 0.5);

    [880, 1175].forEach((frequency, index) => {
      const oscillator = audioContext.createOscillator();
      const toneStartAt = startedAt + index * 0.16;

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, toneStartAt);
      oscillator.connect(gainNode);
      oscillator.start(toneStartAt);
      oscillator.stop(toneStartAt + 0.18);
    });

    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => {});
    }

    window.setTimeout(() => {
      audioContext.close().catch(() => {});
    }, 800);
  } catch {
    // Some browsers block notification audio until the user interacts with the page.
  }
}

export function showBrowserArrivalNotification({
  title,
  body,
  onClick,
  permissionAskedRef
} = {}) {
  if (!("Notification" in window)) {
    return;
  }

  const openNotification = () => {
    const notification = new window.Notification(title || "Notification", {
      body: body || ""
    });

    notification.onclick = () => {
      window.focus();
      if (typeof onClick === "function") {
        onClick();
      }
      notification.close();
    };
  };

  if (window.Notification.permission === "granted") {
    openNotification();
    return;
  }

  if (window.Notification.permission === "default") {
    if (permissionAskedRef?.current) {
      return;
    }

    if (permissionAskedRef) {
      permissionAskedRef.current = true;
    }

    window.Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        openNotification();
      }
    });
  }
}

const LICENSE_REQUEST_CREATOR_NAME = "Hifsa Maryam";
const LICENSE_REQUEST_RECIPIENT_NAMES = ["Waqas ALI", "Shanzay Shabbir"];
const LICENSE_NOTIFICATION_STATE_KEY = "tecgymLicenseRequestNotificationState";

function readPersonName(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "object") {
    return String(
      value.name ||
      value.userName ||
      value.fullName ||
      value.employeeName ||
      value.firstName ||
      value.username ||
      ""
    ).trim();
  }

  return String(value).trim();
}

export function normalizePersonName(value) {
  return readPersonName(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export function personNamesMatch(actualName, expectedName) {
  const actualTokens = normalizePersonName(actualName).split(" ").filter(Boolean);
  const expectedTokens = normalizePersonName(expectedName).split(" ").filter(Boolean);

  if (!actualTokens.length || !expectedTokens.length) {
    return false;
  }

  const actual = actualTokens.join(" ");
  const expected = expectedTokens.join(" ");

  if (actual === expected || actual.startsWith(`${expected} `)) {
    return true;
  }

  return expected.startsWith(`${actual} `)
    && actualTokens.every((token, index) => expectedTokens[index] === token);
}

export function isLicenseRequestNotificationCreator(name) {
  return personNamesMatch(name, LICENSE_REQUEST_CREATOR_NAME);
}

export function isLicenseRequestNotificationRecipient(name) {
  return LICENSE_REQUEST_RECIPIENT_NAMES.some((recipientName) => personNamesMatch(name, recipientName));
}

function unwrapLicenseRequestSource(record) {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return null;
  }

  if (
    record.updateLicenseRequest &&
    typeof record.updateLicenseRequest === "object" &&
    !Array.isArray(record.updateLicenseRequest)
  ) {
    return record.updateLicenseRequest;
  }

  return record;
}

function isDeletedLicenseRequest(source) {
  const deletedFlag = source?.isDelete ?? source?.is_delete ?? source?.deleted;

  return deletedFlag === true || String(deletedFlag || "").trim().toLowerCase() === "true";
}

function normalizeLicenseRequestStatus(status) {
  const value = String(status || "pending")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  if (value === "approved") {
    return "approved";
  }

  if (value === "expire" || value === "expired" || value === "block") {
    return "expire";
  }

  if (value === "deleted") {
    return "deleted";
  }

  return "pending";
}

function getLicenseRequestCollections(response) {
  return [
    response?.updateLicenseRequests,
    response?.data?.updateLicenseRequests,
    response?.response?.updateLicenseRequests,
    response?.data?.items,
    response?.items,
    response?.data,
    response?.response,
    response
  ];
}

export function extractLicenseRequestSnapshots(response) {
  const list = getLicenseRequestCollections(response).find(Array.isArray) || [];
  const seenIds = new Set();
  const snapshots = [];

  list.forEach((record) => {
    const source = unwrapLicenseRequestSource(record);

    if (!source || isDeletedLicenseRequest(source)) {
      return;
    }

    const status = normalizeLicenseRequestStatus(source.status);

    if (status === "deleted") {
      return;
    }

    const id = source._id || source.id;

    if (!id || seenIds.has(String(id))) {
      return;
    }

    seenIds.add(String(id));
    snapshots.push({
      id: String(id),
      status,
      generatedBy: readPersonName(source.generatedBy || source.generated_by)
    });
  });

  return snapshots;
}

export function extractNotificationBranches(response) {
  const list = [
    response,
    response?.response,
    response?.data,
    response?.branches,
    response?.data?.branches,
    response?.response?.branches
  ].find(Array.isArray) || [];
  const seenIds = new Set();
  const branches = [];

  list.forEach((branch) => {
    if (!branch || typeof branch !== "object") {
      return;
    }

    const rawId = branch._id || branch.id;
    const id = typeof rawId === "object"
      ? String(rawId?._id || rawId?.id || "").trim()
      : String(rawId || "").trim();

    if (!id || seenIds.has(id)) {
      return;
    }

    seenIds.add(id);
    branches.push({
      id,
      name: String(branch.name || branch.branchName || "").trim()
    });
  });

  return branches;
}

export function getLicenseNotificationState() {
  try {
    const storedState = window.sessionStorage.getItem(LICENSE_NOTIFICATION_STATE_KEY);

    if (!storedState) {
      return { seededBranchIds: [], requests: [] };
    }

    const parsedState = JSON.parse(storedState);

    return {
      seededBranchIds: Array.isArray(parsedState?.seededBranchIds)
        ? parsedState.seededBranchIds.map(String)
        : [],
      requests: Array.isArray(parsedState?.requests) ? parsedState.requests : []
    };
  } catch {
    return { seededBranchIds: [], requests: [] };
  }
}

export function saveLicenseNotificationState(state) {
  try {
    window.sessionStorage.setItem(LICENSE_NOTIFICATION_STATE_KEY, JSON.stringify({
      seededBranchIds: Array.isArray(state?.seededBranchIds) ? state.seededBranchIds.map(String) : [],
      requests: Array.isArray(state?.requests) ? state.requests : []
    }));
  } catch {
    // Ignore storage failures; the next poll can rebuild the baseline.
  }
}

function isApprovedLicenseSnapshot(snapshot) {
  return snapshot?.status === "approved" || snapshot?.status === "expire";
}

export function diffBranchLicenseNotifications(previousState, branchId, branchName, snapshots) {
  const branchKey = String(branchId || "").trim();
  const state = {
    seededBranchIds: Array.isArray(previousState?.seededBranchIds)
      ? previousState.seededBranchIds.map(String)
      : [],
    requests: Array.isArray(previousState?.requests) ? previousState.requests : []
  };
  const nextSnapshots = (Array.isArray(snapshots) ? snapshots : []).map((snapshot) => ({
    id: String(snapshot?.id || ""),
    status: snapshot?.status || "pending",
    generatedBy: readPersonName(snapshot?.generatedBy),
    branchId: branchKey,
    branchName: String(branchName || "").trim()
  })).filter((snapshot) => snapshot.id);

  if (!branchKey) {
    return { nextState: state, messages: [] };
  }

  const wasSeeded = state.seededBranchIds.includes(branchKey);
  const previousById = new Map(
    state.requests
      .filter((request) => request?.branchId === branchKey)
      .map((request) => [String(request.id), request])
  );
  const messages = [];

  if (wasSeeded) {
    let createdCount = 0;
    let approvedCount = 0;

    nextSnapshots.forEach((snapshot) => {
      if (!isLicenseRequestNotificationCreator(snapshot.generatedBy)) {
        return;
      }

      const previous = previousById.get(snapshot.id);
      const approved = isApprovedLicenseSnapshot(snapshot);

      if (!previous && snapshot.status === "pending") {
        createdCount += 1;
      }

      if ((!previous && approved) || (previous?.status === "pending" && approved)) {
        approvedCount += 1;
      }
    });

    if (createdCount > 0) {
      messages.push({
        type: "created",
        branchId: branchKey,
        branchName: String(branchName || "").trim()
      });
    }

    if (approvedCount > 0) {
      messages.push({
        type: "approved",
        branchId: branchKey,
        branchName: String(branchName || "").trim()
      });
    }
  }

  return {
    nextState: {
      seededBranchIds: wasSeeded ? state.seededBranchIds : [...state.seededBranchIds, branchKey],
      requests: state.requests
        .filter((request) => request?.branchId !== branchKey)
        .concat(nextSnapshots)
    },
    messages
  };
}

function formatBranchNameList(branchNames) {
  const uniqueNames = [...new Set(branchNames.map((name) => String(name || "").trim()).filter(Boolean))];

  if (uniqueNames.length === 0) {
    return "";
  }

  if (uniqueNames.length <= 3) {
    return uniqueNames.join(", ");
  }

  return `${uniqueNames.slice(0, 3).join(", ")} and ${uniqueNames.length - 3} more`;
}

export function describeLicenseRequestNotifications(messages) {
  const notificationMessages = Array.isArray(messages) ? messages : [];
  const created = notificationMessages.filter((message) => message?.type === "created");
  const approved = notificationMessages.filter((message) => message?.type === "approved");
  const parts = [];

  if (created.length > 0) {
    const branchList = formatBranchNameList(created.map((message) => message.branchName));
    parts.push(
      branchList
        ? `Hifsa Maryam created a license update request for ${branchList}.`
        : "Hifsa Maryam created a license update request."
    );
  }

  if (approved.length > 0) {
    const branchList = formatBranchNameList(approved.map((message) => message.branchName));
    parts.push(
      branchList
        ? `A license update request for ${branchList} was approved.`
        : "A license update request was approved."
    );
  }

  return {
    body: parts.join(" "),
    branchId: String((created[0] || approved[0])?.branchId || "")
  };
}

export function getPendingLicenseRequestStorageKey(branchId) {
  return `tecgymPendingLicenseRequestKeys:${String(branchId || "").trim()}`;
}

export function getStoredPendingLicenseRequestKeys(branchId) {
  try {
    const storedKeys = window.sessionStorage.getItem(getPendingLicenseRequestStorageKey(branchId));

    if (!storedKeys) {
      return null;
    }

    const parsedKeys = JSON.parse(storedKeys);
    return Array.isArray(parsedKeys) ? parsedKeys.map(String) : null;
  } catch {
    return null;
  }
}

export function savePendingLicenseRequestKeys(branchId, requestKeys) {
  try {
    window.sessionStorage.setItem(
      getPendingLicenseRequestStorageKey(branchId),
      JSON.stringify(Array.isArray(requestKeys) ? requestKeys.map(String) : [])
    );
  } catch {
    // Ignore storage failures; the badge still updates for the current render.
  }
}

export function extractPendingLicenseRequestKeys(response) {
  return extractLicenseRequestSnapshots(response)
    .filter((snapshot) => snapshot.status === "pending")
    .map((snapshot) => snapshot.id);
}
